'use client';

// Verplaatsbaar zwevend venster (3 sep 2026): sleep aan de kop en het venster
// schuift mee, zodat je midden in een gesprek kunt zien wat eronder staat in
// plaats van op het kruisje te drukken. Eén motor voor het belvenster
// (CallPlusWindow), het meekijkvenster (CallMonitorWindow) en een Modal met
// draggable (het logvenster). Bouwt op usePointerDrag uit lib.js: pointer
// capture, één afhandeling per beeldframe, opruimen bij unmount.
//
// resetKey: verandert die (nieuw gesprek, venster opnieuw open), dan staat het
// venster weer op zijn beginplek. Een sleep die op een knop, link of veld
// begint doet niets, zodat klikken in de kop gewoon blijft werken.
//
// Binnen beeld (29 sep 2026): de kop is de enige greep, dus hij mag nooit uit
// het venster verdwijnen. De sleep houdt de kop helemaal binnen de hoogte van
// het venster en minstens EDGE px binnen de breedte; een kleiner browservenster
// schuift hem terug.

import { useState, useRef, useEffect } from 'react';
import { usePointerDrag } from './lib';

const NO_DRAG = 'button, a, input, select, textarea, [contenteditable], [data-nodrag]';
const EDGE = 80;

// Verschuiving die rect (de kop) binnen beeld brengt, 0 als hij er al staat.
function inView(rect) {
  const w = window.innerWidth, h = window.innerHeight;
  const dx = rect.left > w - EDGE ? w - EDGE - rect.left : rect.right < EDGE ? EDGE - rect.right : 0;
  const dy = rect.top < 0 ? -rect.top : rect.bottom > h ? Math.max(-rect.top, h - rect.bottom) : 0;
  return { dx, dy };
}

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

export default function useWindowDrag(resetKey) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const headRef = useRef(null);
  const core = usePointerDrag();
  useEffect(() => { setOffset({ x: 0, y: 0 }); }, [resetKey]);
  useEffect(() => {
    const onResize = () => {
      const el = headRef.current;
      if (!el || !el.isConnected) return;
      const { dx, dy } = inView(el.getBoundingClientRect());
      if (dx || dy) setOffset((o) => ({ x: o.x + dx, y: o.y + dy }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    if (e.target && typeof e.target.closest === 'function' && e.target.closest(NO_DRAG)) return;
    const start = offsetRef.current;
    const head = e.currentTarget;
    headRef.current = head;
    const r = head.getBoundingClientRect();
    const w = window.innerWidth, h = window.innerHeight;
    core.start(e, {
      cursor: 'grabbing',
      onMove: (dx, dy) => setOffset({
        x: start.x + clamp(dx, EDGE - r.right, w - EDGE - r.left),
        y: start.y + clamp(dy, -r.top, Math.max(-r.top, h - r.bottom)),
      }),
    });
  };
  return { offset, onPointerDown, transform: `translate(${offset.x}px, ${offset.y}px)` };
}
